import { useEffect, useState } from 'react'
import { Brand } from '@/components/Brand'
import { DragGhost } from '@/components/DragGhost'
import { GameBoard } from '@/components/GameBoard'
import { LetterRack } from '@/components/LetterRack'
import { PlayToggle } from '@/components/PlayToggle'
import { ScoreDisplay } from '@/components/ScoreDisplay'
import { currentEvaluation, useGameState } from '@/hooks/useGameState'
import { useKeyboard } from '@/hooks/useKeyboard'
import { TUTORIAL_PUZZLE, TUTORIAL_STEPS, advance, tutorialFeedback } from '@/lib/tutorial'
import { loadWords } from '@/lib/words'

/**
 * A guided practice round on a fixed puzzle, using the real board and rack.
 * The game store runs with saving off, so nothing touches the player's days,
 * and this page never sends scores or events.
 */
export function TutorialPage() {
  const [step, setStep] = useState(0)
  const finished = useGameState((s) => s.record?.finished ?? false)
  const record = useGameState((s) => s.record)
  useKeyboard(true)

  useEffect(() => {
    const game = useGameState.getState()
    game.start(TUTORIAL_PUZZLE, { date: TUTORIAL_PUZZLE.date, attempts: [], finished: false, submitted: false }, { persist: false })
    loadWords()
      .then(game.setDictionary)
      .catch(() => useGameState.setState({ message: 'Could not load the word list. Check your connection and reload.' }))
    try {
      localStorage.setItem('wordevery.seenHelp', '1')
    } catch {
      // Ignore.
    }
  }, [])

  // Move on as the player does what each step asks.
  useEffect(() => {
    return useGameState.subscribe((s) => {
      const watched = {
        selected: s.selected,
        placed: s.placed,
        valid: currentEvaluation(s)?.ok === true,
        finished: s.record?.finished ?? false,
      }
      setStep((current) => advance(current, watched))
    })
  }, [])

  const played = record?.attempts.at(-1)
  const last = step === TUTORIAL_STEPS.length - 1
  const current = last && played ? { ...TUTORIAL_STEPS[step], ...tutorialFeedback(played.score, played.words) } : TUTORIAL_STEPS[step]
  // The challenge step cheers on a play that beats CATS.
  const beat = step === 4 && record?.bestFound && record.bestFound.score > 12 ? record.bestFound : null
  useEffect(() => {
    useGameState.getState().setCoach(TUTORIAL_STEPS[step].coach ?? null)
  }, [step])

  return (
    <div className="page mx-auto flex h-dvh max-w-5xl flex-col overflow-y-auto px-3 pt-3 pb-3 sm:px-6 sm:pt-6">
      <header className="mb-3 flex items-center justify-between gap-2 sm:mb-4">
        <div className="min-w-0">
          <Brand />
          <p className="muted text-sm">How to play</p>
        </div>
        {!last && (
          <a href="/" className="btn-secondary shrink-0 px-3 py-2 text-sm">
            Skip
          </a>
        )}
      </header>

      <section className="panel mb-3 shrink-0 p-4 text-sm" aria-live="polite" aria-labelledby="coach-title">
        <p className="muted text-xs font-semibold tracking-wide uppercase">
          Step {step + 1} of {TUTORIAL_STEPS.length}
        </p>
        <h2 id="coach-title" className="display mt-0.5 text-lg">
          {current.title}
        </h2>
        <p className="mt-1">{current.body}</p>
        {beat && (
          <p className="mt-2 font-semibold">
            <span className="accent-text">
              {beat.words.join(' + ')} for {beat.score} beats CATS!
            </span>{' '}
            Keep looking, or play it.
          </p>
        )}
        {current.next && (
          <button type="button" onClick={() => setStep(step + 1)} className="btn-primary mt-3 w-full sm:w-auto">
            {current.next}
          </button>
        )}
        {last && (
          <div className="mt-3 grid gap-2">
            <PlayToggle />
            <a href="/" className="btn-primary">
              Play today&apos;s puzzle
            </a>
          </div>
        )}
      </section>

      <main className="flex min-h-0 flex-1 flex-col gap-3 md:grid md:grid-cols-[minmax(0,1fr)_22rem] md:gap-10">
        <div className="flex flex-1 flex-col gap-3">
          {!finished && (
            <div className="md:hidden">
              <ScoreDisplay />
            </div>
          )}
          <GameBoard />
        </div>
        <div className="shrink-0 space-y-3">
          {!finished && (
            <div className="hidden md:block">
              <ScoreDisplay />
            </div>
          )}
          <LetterRack />
        </div>
      </main>
      <DragGhost />
    </div>
  )
}
